<template>
  <div class="tracking-page" :class="{ 'trk-page-busy': loading }">
    <tracking-page-header
      title="Problems patients hit"
      subtitle="HTTP 500 errors from booking APIs. 4xx responses and expected lookups are shown on the journey timeline, not here."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="onRangeApply"
      @refresh="loadData"
    />

    <div class="trk-panel mb-3">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Problems in this period</h2>
          <p>{{ formatNumber(totalGroups) }} problem types · {{ formatNumber(errors.totalFailures) }} HTTP 500 events</p>
        </div>
        <div class="trk-panel-actions">
          <button
            class="btn trk-btn-ghost btn-sm"
            type="button"
            :disabled="loading"
            title="Problem list as CSV"
            @click="exportCsv"
          >
            <i class="fa fa-download"></i> CSV
          </button>
        </div>
      </div>
      <div class="trk-panel-body">
        <div class="trk-kpi-grid trk-kpi-grid-compact">
          <div class="trk-kpi" title="HTTP 500 responses recorded during booking">
            <div class="trk-kpi-label">Total problems</div>
            <div class="trk-kpi-value">{{ formatNumber(errors.totalFailures) }}</div>
            <div class="trk-kpi-help">HTTP 500 API errors</div>
          </div>
          <div class="trk-kpi" title="How many distinct 500 fingerprints (error code + step + API)">
            <div class="trk-kpi-label">Problem types</div>
            <div class="trk-kpi-value">{{ formatNumber(totalGroups) }}</div>
            <div class="trk-kpi-help">Different 500 errors</div>
          </div>
          <div class="trk-kpi" title="Backend API calls that returned HTTP 500 during booking">
            <div class="trk-kpi-label">Failed API calls</div>
            <div class="trk-kpi-value">{{ formatNumber(apis.totalErrors) }}</div>
            <div class="trk-kpi-help">{{ formatPct(apis.overallErrorRatePercent) }} of {{ formatNumber(apis.totalCalls) }} calls</div>
          </div>
          <div class="trk-kpi" title="The most common patient-facing problem in this period">
            <div class="trk-kpi-label">Top issue</div>
            <div class="trk-kpi-value trk-kpi-value-compact">{{ topIssue }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-body pb-2">
        <div class="trk-problem-section-tabs" role="tablist" aria-label="Problem category">
          <button v-for="tab in problemSectionTabs" :key="tab.value" type="button" class="trk-section-tab"
                  :class="{ active: problemSection === tab.value }" @click="setProblemSection(tab.value)">
            {{ tab.label }}
          </button>
        </div>
        <p class="text-muted small mb-0 trk-section-tab-help">{{ problemSectionHelp }}</p>
      </div>
    </div>

    <payment-problems-panel v-if="showPaymentProblems" :from-date="fromDate" :to-date="toDate" />
    <otp-problems-panel v-if="showOtpProblems" :from-date="fromDate" :to-date="toDate" />

    <div class="trk-panel mb-3" v-if="showApiProblems">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Problem list</h2>
          <p>HTTP 500s only, sorted by how often they happen</p>
        </div>
      </div>
      <div class="trk-panel-body p-0">
        <div class="trk-table-scroll">
        <table class="table trk-table trk-table-clickable mb-0" v-trk-resizable="'problems-list'">
          <thead>
            <tr>
              <th title="Plain-language problem name">What went wrong</th>
              <th title="Backend endpoint if this was an API failure">API / request</th>
              <th title="Booking step where it happened">Step</th>
              <th title="How many times this fingerprint occurred">Times</th>
              <th title="Share of journeys that hit this problem">Impact</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in problemGroups"
              :key="row.fingerprint"
              @click="openProblem(row)"
              :title="'Click for full details'"
            >
              <td>
                <strong>{{ prettyError(row.errorCode) }}</strong>
                <div class="text-muted small">{{ describeActivity(row.eventName, row).label }}</div>
                <div class="text-danger small" v-if="row.sampleMessage" :title="row.sampleMessage">
                  {{ truncateMsg(row.sampleMessage) }}
                </div>
              </td>
              <td>
                <code v-if="row.apiPath" :title="row.apiPath">{{ row.apiPath }}</code>
                <span v-else class="text-muted">—</span>
                <div class="text-muted small" v-if="row.sampleHttpStatus">HTTP {{ row.sampleHttpStatus }}</div>
              </td>
              <td>{{ prettyStep(row.step) }}</td>
              <td><span class="trk-pill bad">{{ formatNumber(row.count) }}</span></td>
              <td>{{ formatPct(row.ratePercent) }} of journeys</td>
              <td class="text-right" @click.stop>
                <button type="button" class="btn trk-btn-ghost btn-sm" title="Open plain-language explanation" @click="openProblem(row)">Details</button>
              </td>
            </tr>
            <tr v-if="!problemGroups.length">
              <td colspan="6"><div class="trk-empty"><strong>No HTTP 500s found</strong>Great news for this date range.</div></td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      <div class="trk-panel-body d-flex justify-content-between align-items-center flex-wrap" v-if="totalGroups > 0">
        <div class="d-flex align-items-center flex-wrap trk-problems-pager-meta">
          <span class="text-muted small">{{ paginationLabel }}</span>
          <label class="mb-0 text-muted small d-flex align-items-center trk-problems-per-page">
            <span>Per page</span>
            <select class="form-control form-control-sm" :value="size" @change="onSizeChange($event)">
              <option :value="3">3</option>
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
            </select>
          </label>
        </div>
        <b-pagination
          v-model="page"
          :total-rows="totalGroups"
          :per-page="size"
          :disabled="totalPages <= 1"
          @change="onPageChange"
        />
      </div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head trk-panel-head-split">
        <div>
          <h2>Backend APIs with HTTP 500s</h2>
          <p>Every tracked endpoint that returned 500 at least once</p>
        </div>
        <div class="trk-panel-actions">
          <router-link
            class="btn trk-btn-ghost btn-sm"
            :to="{ path: '/tracking/apis', query: { from: fromDate, to: toDate } }"
            title="Open full API performance page"
          >
            All API stats
          </router-link>
        </div>
      </div>
      <div class="trk-panel-body p-0">
        <div class="trk-table-scroll">
        <table class="table trk-table mb-0" v-trk-resizable="'problems-apis'">
          <thead>
            <tr>
              <th title="Backend path patients hit">Endpoint</th>
              <th>Method</th>
              <th title="Total tracked calls">Calls</th>
              <th title="HTTP 500 responses">500s</th>
              <th title="Average response time">Avg time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in failedApis" :key="row.apiPath + row.httpMethod">
              <td><code>{{ row.apiPath }}</code></td>
              <td><span class="trk-pill">{{ row.httpMethod }}</span></td>
              <td>{{ formatNumber(row.callCount) }}</td>
              <td><span class="trk-pill bad">{{ formatNumber(row.errorCount) }} ({{ formatPct(row.errorRatePercent) }})</span></td>
              <td>{{ formatMs(row.avgDurationMs) }}</td>
            </tr>
            <tr v-if="!failedApis.length">
              <td colspan="5"><div class="trk-empty"><strong>No HTTP 500s</strong>Backend requests in this range did not return 500.</div></td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>

    <b-modal
      id="problem-detail-modal"
      v-model="detailOpen"
      title="What this problem means"
      size="lg"
      ok-only
      ok-title="Close"
      ok-variant="success"
      body-class="event-detail-body"
    >
      <div v-if="selected">
        <div class="trk-problem-hero mb-3">
          <span class="trk-pill bad">{{ formatNumber(selected.count) }} times</span>
          <span class="trk-pill info">{{ formatPct(selected.ratePercent) }} of journeys</span>
          <span class="trk-pill">{{ prettyStep(selected.step) }}</span>
        </div>

        <h5 class="trk-problem-title">{{ prettyError(selected.errorCode) }}</h5>
        <p class="trk-problem-lead">{{ problemSummary(selected) }}</p>

        <div class="event-error-box mt-3">
          <h6 class="mb-2">In plain English</h6>
          <div class="meta-item">
            <label>What happened</label>
            <div>{{ whatHappened(selected) }}</div>
          </div>
          <div class="meta-item">
            <label>Where in the booking</label>
            <div>{{ prettyStep(selected.step) }} — {{ prettyEvent(selected.eventName) }}</div>
          </div>
          <div class="meta-item">
            <label>How often</label>
            <div>
              Happened <strong>{{ formatNumber(selected.count) }}</strong> times in this date range
              (about <strong>{{ formatPct(selected.ratePercent) }}</strong> of journeys that started).
            </div>
          </div>
          <div class="meta-item">
            <label>What to check next</label>
            <div>{{ nextAction(selected) }}</div>
          </div>
        </div>

        <gps-ai-feature

          class="mt-3"

          feature="problem"

          :params="{ fingerprint: selected.fingerprint, from: fromDate, to: toDate }"

          title="Diagnose this problem"

          purpose="Finds the most likely root cause of this error from its pattern and samples, and the fix for developers and the team."

          run-label="Diagnose with GPS AI"

        >

          <template v-slot="{ result }">

            <p class="font-weight-bold mb-2">{{ result.headline }} <span class="trk-pill ml-1">{{ result.severity }}</span></p>

            <div class="mb-1"><strong>Root cause:</strong> {{ result.rootCause }}</div>

            <div class="mb-1"><strong>Evidence:</strong> {{ result.evidence }}</div>

            <div class="mb-1"><strong>Who is affected:</strong> {{ result.whoIsAffected }}</div>

            <div class="mb-1"><strong>Developer fix:</strong>

              <ol class="mb-1 pl-3"><li v-for="(step, i) in result.developerFix || []" :key="i">{{ step }}</li></ol>

            </div>

            <div><strong>Team action:</strong> {{ result.teamAction }}</div>

          </template>

        </gps-ai-feature>


        <div class="mt-3">
          <h6 class="mb-2">Technical details</h6>
          <table class="table table-sm event-meta-table mb-0">
            <tbody>
              <tr>
                <th>Error code</th>
                <td><code>{{ selected.errorCode || 'UNSPECIFIED' }}</code></td>
              </tr>
              <tr>
                <th>Event</th>
                <td>{{ selected.eventName || '—' }}</td>
              </tr>
              <tr>
                <th>Step code</th>
                <td>{{ selected.step || '—' }}</td>
              </tr>
              <tr>
                <th>API path</th>
                <td>
                  <code v-if="selected.apiPath">{{ selected.apiPath }}</code>
                  <span v-else class="text-muted">Not an API failure (or path not recorded)</span>
                </td>
              </tr>
              <tr v-if="selected.sampleHttpStatus">
                <th>HTTP status</th>
                <td>{{ selected.sampleHttpStatus }}</td>
              </tr>
              <tr v-if="selected.sampleMessage">
                <th>Backend / client message</th>
                <td class="text-break">{{ selected.sampleMessage }}</td>
              </tr>
              <tr v-if="problemCurl(selected)">
                <th>CURL request</th>
                <td>
                  <div class="d-flex justify-content-between align-items-start mb-1">
                    <span class="text-muted small">Reproduce the failed patient API call</span>
                    <button type="button" class="btn btn-sm trk-btn-ghost" @click="copyCurl(selected)">Copy</button>
                  </div>
                  <pre class="api-payload-curl mb-0">{{ problemCurl(selected) }}</pre>
                </td>
              </tr>
              <tr>
                <th>Fingerprint</th>
                <td class="text-break"><code>{{ selected.fingerprint || '—' }}</code></td>
              </tr>
              <tr>
                <th>Date range</th>
                <td>{{ fromDate }} → {{ toDate }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="trk-problem-actions mt-3">
          <router-link class="btn trk-btn-primary btn-sm" to="/tracking/issues">Track a fix</router-link>
          <router-link
            v-if="selected.apiPath"
            class="btn trk-btn-ghost btn-sm"
            :to="{ path: '/tracking/apis', query: { from: fromDate, to: toDate } }"
          >
            Open API performance
          </router-link>
          <router-link
            class="btn trk-btn-ghost btn-sm"
            :to="{ path: '/tracking/sessions' }"
          >
            Browse journeys
          </router-link>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import GpsAiFeature from './GpsAiFeature.vue';
import PaymentProblemsPanel from './PaymentProblemsPanel.vue';
import OtpProblemsPanel from './OtpProblemsPanel.vue';
import { buildEventApiCurl } from '../../utils/buildCurlCommand';
import {
  defaultDateRange,
  describeActivity,
  formatNumber,
  formatPct,
  isExpectedLookupMiss,
  prettyError,
  prettyEvent,
  prettyStep,
  rangeParams
} from './trackingShared';

const PATIENT_API_BASE =
  process.env.VUE_APP_PATIENT_API || process.env.VUE_APP_GPS_API || 'http://localhost:8080';

export default {
  name: 'TrackingErrors',
  components: { TrackingPageHeader, PaymentProblemsPanel, OtpProblemsPanel, GpsAiFeature },
  data() {
    const range = defaultDateRange(7);
    const section = this.$route.query.section;
    const problemSection = ['all', 'payment', 'otp', 'api'].includes(section) ? section : 'all';
    return {
      problemSection,
      fromDate: this.$route.query.from || range.fromDate,
      toDate: this.$route.query.to || range.toDate,
      errors: {},
      apis: { totalCalls: 0, totalErrors: 0, overallErrorRatePercent: 0, endpoints: [] },
      loading: false,
      detailOpen: false,
      selected: null,
      page: Number(this.$route.query.page) > 0 ? Number(this.$route.query.page) : 1,
      size: 20,
      openFingerprint: this.$route.query.fingerprint || null
    };
  },
  computed: {
    problemSectionTabs() {
      return [
        { value: 'all', label: 'All problems' },
        { value: 'payment', label: 'Payment failures' },
        { value: 'otp', label: 'OTP / email codes' },
        { value: 'api', label: 'HTTP 500 (API)' }
      ];
    },
    problemSectionHelp() {
      const map = {
        all: 'Payment declines, email OTP issues, and server errors in one place.',
        payment: 'Card and checkout payment failures only.',
        otp: 'Verification codes — sent, failed, or verified (checkout / existing account).',
        api: 'Backend HTTP 500 errors during booking.'
      };
      return map[this.problemSection] || map.all;
    },
    showPaymentProblems() {
      return this.problemSection === 'all' || this.problemSection === 'payment';
    },
    showOtpProblems() {
      return this.problemSection === 'all' || this.problemSection === 'otp';
    },
    showApiProblems() {
      return this.problemSection === 'all' || this.problemSection === 'api';
    },
    problemGroups() {
      return (this.errors.groups || []).filter((g) => {
        if (g.sampleHttpStatus != null && Number(g.sampleHttpStatus) < 500) {
          return false;
        }
        return !isExpectedLookupMiss(g.apiPath, g.sampleHttpStatus, g.errorCode)
          && !isExpectedLookupMiss(g.apiPath, g.sampleHttpStatus, g.sampleMessage);
      });
    },
    topIssue() {
      if (this.errors.topErrorCode) {
        return prettyError(this.errors.topErrorCode);
      }
      const g = this.problemGroups[0];
      return g ? prettyError(g.errorCode) : 'None';
    },
    chatFilters() {
      return rangeParams(this.fromDate, this.toDate);
    },
    pageSummary() {
      return `${this.formatNumber(this.errors.totalFailures)} HTTP 500s across ${this.formatNumber(this.totalGroups)} types; ${this.formatNumber(this.apis.totalErrors)} failed API calls; top issue: ${this.topIssue}`;
    },
    totalGroups() {
      const reported = Number(this.errors.totalGroups);
      return Number.isFinite(reported) ? reported : this.problemGroups.length;
    },
    totalPages() {
      const reported = Number(this.errors.totalPages);
      if (Number.isFinite(reported) && reported > 0) {
        return reported;
      }
      return this.totalGroups > 0 ? Math.ceil(this.totalGroups / this.size) : 0;
    },
    paginationLabel() {
      if (!this.totalGroups) {
        return '';
      }
      const from = (this.page - 1) * this.size + 1;
      const to = Math.min(this.page * this.size, this.totalGroups);
      const range = from === to ? `${from}` : `${from}–${to}`;
      return `Showing ${range} of ${this.formatNumber(this.totalGroups)} · Page ${this.page} of ${this.totalPages}`;
    },
    failedApis() {
      return (this.apis.endpoints || [])
        .filter((row) => Number(row.errorCount || 0) > 0)
        .slice(0, 25);
    }
  },
  created() { this.loadData({ locateFingerprint: this.openFingerprint }); },
  methods: {
    formatNumber,
    formatPct,
    prettyError,
    prettyEvent,
    prettyStep,
    describeActivity,
    truncateMsg(msg) {
      const text = String(msg || '');
      return text.length > 140 ? text.slice(0, 140) + '…' : text;
    },
    formatMs(v) {
      if (v == null || Number.isNaN(Number(v))) return '—';
      const n = Number(v);
      if (n < 1000) return `${Math.round(n)} ms`;
      return `${(n / 1000).toFixed(1)} s`;
    },
    problemCurl(row) {
      if (!row || !row.apiPath) {
        return '';
      }
      return buildEventApiCurl({
        apiPath: row.apiPath,
        metadata: {
          httpMethod: row.sampleHttpMethod || 'GET',
          requestPayload: row.sampleRequestPayload,
          queryParams: row.sampleQueryParams
        }
      }, PATIENT_API_BASE);
    },
    copyCurl(row) {
      const curl = this.problemCurl(row);
      if (!curl || !navigator.clipboard) {
        return;
      }
      navigator.clipboard.writeText(curl).then(() => {
        if (this.$awn) {
          this.$awn.success('CURL copied');
        }
      }).catch(() => {});
    },
    openProblem(row) {
      this.selected = row;
      this.detailOpen = true;
      if (row && row.fingerprint) {
        const query = Object.assign({}, this.$route.query, {
          fingerprint: row.fingerprint,
          page: this.page,
          from: this.fromDate,
          to: this.toDate
        });
        this.$router.replace({ query }).catch(() => {});
      }
    },
    problemSummary(row) {
      const name = prettyError(row.errorCode);
      const step = prettyStep(row.step);
      return `${name} showed up during “${step}”, about ${formatPct(row.ratePercent)} of journeys in this period.`;
    },
    whatHappened(row) {
      if (row.sampleMessage) {
        return `Backend/client message: ${row.sampleMessage}`;
      }
      const code = String(row.errorCode || '').toUpperCase();
      const event = String(row.eventName || '').toUpperCase();
      const path = row.apiPath || '';

      if (code === 'CARD_DECLINED' || code === 'PAYMENT_FAILED' || event === 'PAYMENT_FAILED') {
        return 'The patient tried to pay, but the payment did not succeed. They may have seen a decline message or a generic payment error.';
      }
      if (code === 'OTP_INVALID' || code === 'OTP_FAILED' || event.indexOf('OTP') >= 0) {
        return 'Phone or email verification failed — wrong code, expired code, or the verification service returned an error.';
      }
      if (code === 'NO_SLOTS' || code === 'SLOT_LOAD_FAILED' || event.indexOf('SLOT') >= 0) {
        return 'Appointment times could not be shown or chosen — no slots available, or the slot list failed to load.';
      }
      if (code === 'VALIDATION_FAILED' || event === 'DETAILS_FAILED') {
        return 'The details form was incomplete or invalid, so the patient could not continue.';
      }
      if (code === 'ASSESSMENT_INVALID' || event === 'ASSESSMENT_FAILED') {
        return 'Health questions were incomplete or rejected, so the patient stalled on the assessment step.';
      }
      if (code === 'REGISTRATION_FAILED' || event === 'REGISTRATION_FAILED') {
        return 'Account creation failed, so the patient could not finish signing up.';
      }
      if (event === 'API_CALL' || path) {
        const status = row.sampleHttpStatus ? ` HTTP ${row.sampleHttpStatus}` : '';
        return path
          ? `The backend returned${status || ' HTTP 500'} for ${path} while the patient was booking.`
          : `A backend API returned${status || ' HTTP 500'} during booking.`;
      }
      if (code === 'UNKNOWN_PATIENT' || /unknown patient/i.test(prettyError(row.errorCode))) {
        return 'The system could not find a patient record for an identifier that should already exist. Expected “no account for this email” lookups are not listed here.';
      }
      if (code === 'UNSPECIFIED' || code === 'UNKNOWN' || code === 'UNKNOWN_ERROR' || !code) {
        return 'A failure was recorded, but the system did not get a clear error code. Check the event type and step below, then inspect recent journeys or API performance for matching failures.';
      }
      return `Patients hit “${prettyError(row.errorCode)}” while ${prettyEvent(row.eventName).toLowerCase()}. Use the technical details below to investigate.`;
    },
    nextAction(row) {
      const code = String(row.errorCode || '').toUpperCase();
      const event = String(row.eventName || '').toUpperCase();
      const path = row.apiPath || '';

      if (code === 'CARD_DECLINED' || code === 'PAYMENT_FAILED' || event === 'PAYMENT_FAILED') {
        return 'Check Stripe / payment logs for declines, then open a few failed journeys around payment to confirm the patient message.';
      }
      if (code === 'OTP_INVALID' || code === 'OTP_FAILED' || event.indexOf('OTP') >= 0) {
        return 'Check OTP delivery and expiry. Look at Account / OTP journeys that failed recently.';
      }
      if (code === 'NO_SLOTS' || code === 'SLOT_LOAD_FAILED') {
        return 'Open Doctor slots for this date range and confirm supply for the times patients were trying to book.';
      }
      if (path) {
        return `Open API performance and inspect ${path}. Then open a journey and use 500s to see the request/response.`;
      }
      if (code === 'UNSPECIFIED' || code === 'UNKNOWN_ERROR' || code === 'UNKNOWN') {
        return 'Open Live now / All journeys, filter for Failed results on this step, and read the timeline for the real error message.';
      }
      return 'Track a fix on the Fixes page so you can see whether this problem drops after your change.';
    },
    onRangeApply(range) {
      this.fromDate = range.fromDate;
      this.toDate = range.toDate;
      this.page = 1;
      const query = Object.assign({}, this.$route.query, {
        page: 1,
        from: this.fromDate,
        to: this.toDate
      });
      delete query.fingerprint;
      this.$router.replace({ query }).catch(() => {});
      this.loadData();
    },
    maybeOpenFingerprint() {
      const fp = this.openFingerprint;
      if (!fp) {
        return;
      }
      const match = this.problemGroups.find((g) => g.fingerprint === fp);
      if (match) {
        this.openProblem(match);
      }
      this.openFingerprint = null;
    },
    onPageChange(page) {
      this.page = page;
      const query = Object.assign({}, this.$route.query, {
        page: this.page,
        from: this.fromDate,
        to: this.toDate
      });
      this.$router.replace({ query }).catch(() => {});
      this.loadData({ errorsOnly: true });
    },
    onSizeChange(event) {
      const next = Number(event && event.target ? event.target.value : this.size);
      this.size = Number.isFinite(next) && next > 0 ? next : 20;
      this.page = 1;
      const query = Object.assign({}, this.$route.query, {
        page: 1,
        from: this.fromDate,
        to: this.toDate
      });
      this.$router.replace({ query }).catch(() => {});
      this.loadData({ errorsOnly: true });
    },
    loadData(options = {}) {
      const errorsOnly = options.errorsOnly === true;
      const locateFingerprint = options.locateFingerprint;
      this.loading = true;
      const params = Object.assign({}, rangeParams(this.fromDate, this.toDate), {
        page: Math.max(0, this.page - 1),
        size: this.size
      });
      if (locateFingerprint) {
        params.fingerprint = locateFingerprint;
      }
      const requests = [this.$onboardingTrackingService.getErrors(params)];
      if (!errorsOnly) {
        requests.push(this.$onboardingTrackingService.getApis(rangeParams(this.fromDate, this.toDate)));
      }
      Promise.all(requests).then(([errRes, apiRes]) => {
        const payload = (errRes && errRes.data) || errRes || {};
        this.errors = payload;
        if (locateFingerprint && payload.page != null && Number.isFinite(Number(payload.page))) {
          this.page = Number(payload.page) + 1;
          const query = Object.assign({}, this.$route.query, {
            page: this.page,
            fingerprint: locateFingerprint,
            from: this.fromDate,
            to: this.toDate
          });
          this.$router.replace({ query }).catch(() => {});
        }
        if (payload.size != null && Number(payload.size) > 0) {
          this.size = Number(payload.size);
        }
        if (apiRes) {
          this.apis = (apiRes && apiRes.data) || apiRes || this.apis;
        }
        this.$nextTick(() => this.maybeOpenFingerprint());
      }).finally(() => { this.loading = false; });
    },
    exportCsv() {
      this.$onboardingTrackingService.exportSessions(rangeParams(this.fromDate, this.toDate));
    },
    setProblemSection(section) {
      if (this.problemSection === section) {
        return;
      }
      this.problemSection = section;
      const query = Object.assign({}, this.$route.query, { section });
      this.$router.replace({ query }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.trk-problems-pager-meta {
  gap: 0.75rem;
}
.trk-problems-per-page {
  gap: 0.35rem;
}
.trk-problems-per-page select {
  width: auto;
  min-width: 4.25rem;
}
.trk-problem-section-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.trk-section-tab {
  border: 1px solid #dfe7e3;
  background: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.85rem;
  font-size: 0.9rem;
  cursor: pointer;
}
.trk-section-tab.active {
  background: #17333a;
  border-color: #17333a;
  color: #fff;
}
.trk-section-tab-help {
  margin-top: 0.25rem;
}
</style>
